import { isCriticalKpiId } from "../../criticalKpi";
import type { BusinessService, Kpi, Status } from "../../types";

const ALERT_RISK_FACTOR: Record<Status, number> = {
  healthy: 0,
  warning: 0.55,
  critical: 1,
};

const RISK_FROM_SCORE_WEIGHT = 0.65;
const RISK_FROM_DEGRADED_KPI_WEIGHT = 25;
const RISK_FROM_ACTIVE_ALERTS_WEIGHT = 20;
const RISK_FROM_BUSINESS_WEIGHT = 15;
const SLO_FROM_SCORE_WEIGHT = 0.85;
const SLO_FROM_ALERTS_WEIGHT = 15;
const DEPENDENCY_IMPACT_EXPONENT = 0.6;

const clampInt = (value: number): number => {
  const next = Math.round(value);
  if (next < 0) {
    return 0;
  }
  if (next > 100) {
    return 100;
  }
  return next;
};

const clamp01 = (value: number): number => {
  if (value < 0) {
    return 0;
  }
  if (value > 1) {
    return 1;
  }
  return value;
};

const clamp100 = (value: number): number => {
  if (value < 0) {
    return 0;
  }
  if (value > 100) {
    return 100;
  }
  return value;
};

const scoreToStatus = (score: number): Status => {
  if (score < 50) {
    return "critical";
  }
  if (score < 75) {
    return "warning";
  }
  return "healthy";
};

const kpiThresholdRiskMultiplier = (kpi: Kpi): number => {
  const warningThreshold = clamp100(kpi.thresholds.warning);
  const criticalThreshold = clamp100(kpi.thresholds.critical);
  const thresholdMidpoint = (warningThreshold + criticalThreshold) / 2;

  // 50 is neutral (x1), 0 is strict (x1.5), 100 is tolerant (x0.5).
  return 1 + (50 - thresholdMidpoint) / 100;
};

const computeKpiMetrics = (
  kpi: Kpi,
  statusByAlertId: Map<string, Status>,
): Kpi => {
  const alertIds = Array.from(new Set(kpi.alerts ?? []));
  const connectionsByAlertId = new Map(
    (kpi.alertConnections ?? []).map((connection) => [connection.alertId, connection]),
  );

  const weightedEntries: { risk: number; weight: number }[] = [];
  let activeAlerts = 0;
  let activeWarningAlerts = 0;
  let activeCriticalAlerts = 0;

  for (const alertId of alertIds) {
    const status = statusByAlertId.get(alertId) ?? "healthy";
    const connection = connectionsByAlertId.get(alertId);
    const weight = clamp100(connection?.weight ?? 0);
    weightedEntries.push({
      risk: ALERT_RISK_FACTOR[status],
      weight,
    });

    if (status === "warning") {
      activeAlerts += 1;
      activeWarningAlerts += 1;
    } else if (status === "critical") {
      activeAlerts += 1;
      activeCriticalAlerts += 1;
    }
  }

  const weightedRisk = (() => {
    if (weightedEntries.length === 0) {
      return 0;
    }

    const configuredWeightTotal = weightedEntries.reduce(
      (sum, entry) => sum + entry.weight,
      0,
    );

    if (configuredWeightTotal <= 0) {
      const fallbackTotal = weightedEntries.length;
      const fallbackRisk = weightedEntries.reduce(
        (sum, entry) => sum + entry.risk,
        0,
      );
      return fallbackRisk / fallbackTotal;
    }

    const weightedNumerator = weightedEntries.reduce(
      (sum, entry) => sum + entry.risk * entry.weight,
      0,
    );
    return weightedNumerator / configuredWeightTotal;
  })();

  const baseRisk = weightedRisk;
  const thresholdMultiplier = kpiThresholdRiskMultiplier(kpi);
  const risk = clamp01(baseRisk * thresholdMultiplier);
  const score = clampInt(100 * (1 - risk));
  const status = scoreToStatus(score);

  return {
    ...kpi,
    metrics: {
      ...(kpi.metrics ?? {}),
      score,
      status,
      linkedAlerts: alertIds.length,
      activeAlerts,
      activeWarningAlerts,
      activeCriticalAlerts,
    },
  };
};

const computeWeight = (kpis: Kpi[], target: Kpi): number => {
  const positiveWeightSum = kpis.reduce((sum, kpi) => {
    const weight = kpi.weight ?? 0;
    return weight > 0 ? sum + weight : sum;
  }, 0);

  if (positiveWeightSum <= 0) {
    return kpis.length > 0 ? 1 / kpis.length : 0;
  }

  const nextWeight = target.weight ?? 0;
  if (nextWeight <= 0) {
    return 0;
  }
  return nextWeight / positiveWeightSum;
};

type KpiRollup = {
  linkedAlerts: number;
  activeAlerts: number;
  kpiHealthy: number;
  kpiWarning: number;
  kpiCritical: number;
  degradedKpiRatio: number;
  activeAlertsRatio: number;
};

const summarizeKpis = (kpis: Kpi[]): KpiRollup => {
  let kpiWarning = 0;
  let kpiCritical = 0;
  let linkedAlerts = 0;
  let activeAlerts = 0;

  for (const kpi of kpis) {
    const kpiStatus = kpi.metrics?.status ?? "healthy";
    if (kpiStatus === "warning") {
      kpiWarning += 1;
    } else if (kpiStatus === "critical") {
      kpiCritical += 1;
    }
    linkedAlerts += kpi.metrics?.linkedAlerts ?? 0;
    activeAlerts += kpi.metrics?.activeAlerts ?? 0;
  }

  const kpiHealthy = kpis.length - kpiWarning - kpiCritical;
  const degradedKpiRatio = kpis.length > 0 ? (kpiWarning + kpiCritical) / kpis.length : 0;
  const activeAlertsRatio = linkedAlerts > 0 ? activeAlerts / linkedAlerts : 0;

  return {
    linkedAlerts,
    activeAlerts,
    kpiHealthy,
    kpiWarning,
    kpiCritical,
    degradedKpiRatio,
    activeAlertsRatio,
  };
};

const computeBusinessWeight = (service: BusinessService): number => {
  return clamp01(((service.businessCritical ?? 0) + (service.revenueWeight ?? 0)) / 2);
};

const computeRiskForecast = (
  score: number,
  degradedKpiRatio: number,
  activeAlertsRatio: number,
  businessWeight: number,
): number => {
  return clampInt(
    (100 - score) * RISK_FROM_SCORE_WEIGHT +
      degradedKpiRatio * RISK_FROM_DEGRADED_KPI_WEIGHT +
      activeAlertsRatio * RISK_FROM_ACTIVE_ALERTS_WEIGHT +
      businessWeight * RISK_FROM_BUSINESS_WEIGHT,
  );
};

const computeSlo = (score: number, activeAlertsRatio: number): number => {
  return clampInt(
    score * SLO_FROM_SCORE_WEIGHT + (1 - activeAlertsRatio) * SLO_FROM_ALERTS_WEIGHT,
  );
};

const applyServiceScore = (
  service: BusinessService,
  score: number,
  kpis: Kpi[],
): BusinessService => {
  const rollup = summarizeKpis(kpis);
  const businessWeight = computeBusinessWeight(service);
  const riskForecast = computeRiskForecast(
    score,
    rollup.degradedKpiRatio,
    rollup.activeAlertsRatio,
    businessWeight,
  );
  const slo = computeSlo(score, rollup.activeAlertsRatio);
  const status = scoreToStatus(score);

  return {
    ...service,
    kpis,
    status,
    metrics: {
      ...(service.metrics ?? {}),
      score,
      riskForecast,
      slo,
      linkedAlerts: rollup.linkedAlerts,
      activeAlerts: rollup.activeAlerts,
      kpiHealthy: rollup.kpiHealthy,
      kpiWarning: rollup.kpiWarning,
      kpiCritical: rollup.kpiCritical,
    },
  };
};

export const computeServiceFromAlertStatuses = (
  service: BusinessService,
  statusByAlertId: Map<string, Status>,
): BusinessService => {
  const nextKpis = (service.kpis ?? []).map((kpi) =>
    computeKpiMetrics(kpi, statusByAlertId),
  );

  const regularKpis = nextKpis.filter((kpi) => !isCriticalKpiId(kpi.id));
  const criticalKpi = nextKpis.find((kpi) => isCriticalKpiId(kpi.id));

  const weightedRegularKpiScore = regularKpis.reduce((sum, kpi) => {
    const score = clampInt(kpi.metrics?.score ?? 100);
    return sum + score * computeWeight(regularKpis, kpi);
  }, 0);

  const regularScore = regularKpis.length > 0 ? clampInt(weightedRegularKpiScore) : 100;
  const criticalScore = clampInt(criticalKpi?.metrics?.score ?? 100);
  const score = Math.min(regularScore, criticalScore);
  return applyServiceScore(service, score, nextKpis);
};

export const applyDependencyScoreImpact = (
  service: BusinessService,
  dependencyScores: number[],
): BusinessService => {
  if (dependencyScores.length === 0) {
    return service;
  }

  const baseScore = clampInt(service.metrics?.score ?? 100);
  const dependencyProduct = dependencyScores.reduce((acc, depScore) => {
    return acc * (clamp100(depScore) / 100);
  }, 1);
  const dependencyFactor = Math.pow(dependencyProduct, DEPENDENCY_IMPACT_EXPONENT);
  const score = clampInt(baseScore * dependencyFactor);

  return applyServiceScore(service, score, service.kpis ?? []);
};
